import {
  ArrowLeftOutlined,
  LeftOutlined,
  LinkOutlined,
  MoreOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, DatePicker, Dropdown, List, Segmented, Tag, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import { apiErrorMessage } from '../api/client';
import { operationsApi, reportsApi, skladsApi, storesApi } from '../api/endpoints';
import { invalidateMoneyData, queryKeys } from '../api/queryKeys';
import type { Operation } from '../api/types';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/PageStates';
import { OperationEditModal } from '../components/OperationEditModal';
import { OpTypeTag } from '../components/OpTypeTag';
import { ActorLine } from '../components/ActorLine';
import { PaymentMethodTag } from '../components/PaymentMethodTag';
import { useStoreContext } from '../context/StoreContext';
import { balanceColor, OWED_TO_OWNER, OWNER_OWES } from '../lib/balanceColors';
import { formatKopeks } from '../lib/money';
import { monthFromSearch } from '../lib/returnTo';
import { SkladDayGridTable } from './daygrid/SkladDayGridTable';
import { StoreDayGridTable } from './daygrid/StoreDayGridTable';

type LedgerView = 'list' | 'grid';
const VIEW_KEY = 'riba.ledgerView';

/**
 * /stores/:id and /sklads/:id monthly ledger (STEP-06): opening balance row,
 * chronological operations with signed amounts and the running balance after
 * each row, closing balance footer. Paired operations carry a pairId badge;
 * a row menu offers edit/delete.
 */
export function LedgerPage({ kind }: { kind: 'store' | 'sklad' }): JSX.Element {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { storeId: ctxStoreId } = useStoreContext();
  const { id = '' } = useParams<{ id: string }>();
  // `?month=` / `?view=` let a накладная screen bring the user back to the very
  // jadval (month + table view) it was opened from; otherwise: now, last view.
  const { search } = useLocation();
  const [month, setMonth] = useState<Dayjs>(() => monthFromSearch(search));
  const [editing, setEditing] = useState<Operation | null>(null);
  const [view, setView] = useState<LedgerView>(() => {
    const fromUrl = new URLSearchParams(search).get('view');
    if (fromUrl === 'grid' || fromUrl === 'list') return fromUrl;
    return (localStorage.getItem(VIEW_KEY) as LedgerView | null) ?? 'list';
  });
  const changeView = (next: LedgerView): void => {
    setView(next);
    localStorage.setItem(VIEW_KEY, next);
  };

  const monthStr = month.format('YYYY-MM');
  const ledgerQuery = useQuery({
    queryKey: queryKeys.ledger(kind, id, monthStr),
    queryFn: () =>
      kind === 'store' ? reportsApi.storeLedger(id, monthStr) : reportsApi.skladLedger(id, monthStr),
    enabled: id !== '' && view === 'list',
  });

  // Entity name + live balance for the page header (archived ones included —
  // history stays viewable after archiving).
  const storesQuery = useQuery({
    queryKey: queryKeys.stores(true),
    queryFn: () => storesApi.list(true),
    enabled: kind === 'store',
  });
  const skladsQuery = useQuery({
    queryKey: queryKeys.sklads(true),
    queryFn: () => skladsApi.list(true),
    enabled: kind === 'sklad',
  });
  const entity =
    kind === 'store'
      ? storesQuery.data?.find((s) => s.id === id)
      : skladsQuery.data?.find((s) => s.id === id);
  const entityMoney = entity ? (kind === 'store' ? ('balance' in entity ? entity.balance : 0) : ('debt' in entity ? entity.debt : 0)) : null;

  const deleteMutation = useMutation({
    mutationFn: (operationId: string) => operationsApi.remove(operationId),
    onSuccess: () => {
      message.success(t('ledger.deleted'));
      invalidateMoneyData(queryClient);
    },
    onError: (error) => message.error(apiErrorMessage(error) ?? t('common.requestError')),
  });

  const confirmDelete = (operation: Operation): void => {
    modal.confirm({
      title: t('ledger.deleteConfirmTitle'),
      content: t('ledger.deleteConfirmText'),
      okText: t('common.delete'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: () => deleteMutation.mutateAsync(operation.id).then(() => undefined),
    });
  };

  const plusType = kind === 'store' ? 'ISSUE' : 'PURCHASE';
  const showBack = !(kind === 'store' && ctxStoreId === id);

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {showBack ? (
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label={t('common.back')}
            onClick={() => navigate(kind === 'store' ? '/stores' : '/sklads')}
          />
        ) : null}
        <Typography.Title level={4} style={{ margin: 0, flex: 1 }}>
          {entity?.name ?? '…'}
        </Typography.Title>
        {entityMoney !== null ? (
          <Typography.Text strong style={{ fontSize: 17, color: balanceColor(kind, entityMoney) }}>
            {formatKopeks(entityMoney)}
          </Typography.Text>
        ) : null}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button icon={<LeftOutlined />} onClick={() => setMonth((m) => m.subtract(1, 'month'))} />
        <DatePicker
          picker="month"
          value={month}
          onChange={(m) => m && setMonth(m.startOf('month'))}
          allowClear={false}
          format="MMMM YYYY"
          style={{ flex: 1 }}
          inputReadOnly
        />
        <Button icon={<RightOutlined />} onClick={() => setMonth((m) => m.add(1, 'month'))} />
      </div>

      <Segmented<LedgerView>
        block
        value={view}
        onChange={changeView}
        options={[
          { label: t('daygrid.viewList'), value: 'list' },
          { label: t('daygrid.viewGrid'), value: 'grid' },
        ]}
      />

      {view === 'grid' ? (
        kind === 'store' ? (
          <StoreDayGridTable id={id} month={monthStr} />
        ) : (
          <SkladDayGridTable id={id} month={monthStr} />
        )
      ) : ledgerQuery.isLoading ? (
        <LoadingBlock />
      ) : ledgerQuery.isError || !ledgerQuery.data ? (
        <ErrorBlock onRetry={() => void ledgerQuery.refetch()} />
      ) : (
        <Card size="small" styles={{ body: { paddingTop: 4, paddingBottom: 4 } }}>
          {/* Not a List.Item: outside a <List> it loses the flex styles and the
              label glues to the amount. */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 8,
              padding: '12px 4px',
            }}
          >
            <Typography.Text type="secondary">{t('ledger.openingBalance')}</Typography.Text>
            <Typography.Text strong>{formatKopeks(ledgerQuery.data.openingBalance)}</Typography.Text>
          </div>
          {ledgerQuery.data.rows.length === 0 ? (
            <EmptyBlock description={t('ledger.empty')} />
          ) : (
            <List
              dataSource={ledgerQuery.data.rows}
              renderItem={({ operation, runningBalance }) => {
                const positive = operation.type === plusType;
                return (
                  <List.Item
                    style={{ paddingInline: 4, alignItems: 'flex-start' }}
                    extra={
                      <Dropdown
                        trigger={['click']}
                        menu={{
                          // A накладная-backed row is document-owned: its amount can
                          // only change by re-pricing the накладная, so offer the doc
                          // link instead of raw edit/delete.
                          items: operation.shipmentId
                            ? [{ key: 'open', label: t('shipments.openDoc') }]
                            : [
                                { key: 'edit', label: t('common.edit') },
                                { key: 'delete', label: t('common.delete'), danger: true },
                              ],
                          onClick: ({ key }) => {
                            if (key === 'open') navigate(`/shipments/${operation.shipmentId}`);
                            else if (key === 'edit') setEditing(operation);
                            else confirmDelete(operation);
                          },
                        }}
                      >
                        <Button type="text" icon={<MoreOutlined />} aria-label={t('common.edit')} />
                      </Dropdown>
                    }
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <Typography.Text type="secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {dayjs(operation.date).format('DD.MM')}
                        </Typography.Text>
                        <OpTypeTag type={operation.type} />
                        <PaymentMethodTag method={operation.paymentMethod} />
                        {operation.deductFromStoreBalance ? (
                          <Tag color="volcano" style={{ marginInlineEnd: 0 }}>
                            {t('quick.deductFromStore')}
                          </Tag>
                        ) : null}
                        {operation.pairId ? (
                          <Tag icon={<LinkOutlined />} color="purple" style={{ marginInlineEnd: 0 }}>
                            {t('common.pairTag')}
                          </Tag>
                        ) : null}
                      </div>
                      {kind === 'sklad' && operation.type === 'PURCHASE' && operation.store ? (
                        <Typography.Text type="secondary" style={{ display: 'block', fontSize: 13 }}>
                          → {operation.store.name}
                        </Typography.Text>
                      ) : null}
                      {operation.comment ? (
                        <Typography.Text type="secondary" style={{ display: 'block', fontSize: 13 }} ellipsis>
                          {operation.comment}
                        </Typography.Text>
                      ) : null}
                      <ActorLine
                        enteredBy={operation.enteredByName}
                        approvedBy={operation.approvedByName}
                      />
                    </div>
                    <div style={{ textAlign: 'right', marginInlineStart: 8 }}>
                      <Typography.Text strong style={{ color: positive ? OWED_TO_OWNER : OWNER_OWES }}>
                        {positive ? '+' : '−'}
                        {formatKopeks(operation.amount)}
                      </Typography.Text>
                      <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                        {formatKopeks(runningBalance)}
                      </Typography.Text>
                    </div>
                  </List.Item>
                );
              }}
            />
          )}
          <List.Item style={{ paddingInline: 4 }}>
            <Typography.Text type="secondary">{t('ledger.closingBalance')}</Typography.Text>
            <Typography.Text
              strong
              style={{ fontSize: 16, color: balanceColor(kind, ledgerQuery.data.closingBalance) }}
            >
              {formatKopeks(ledgerQuery.data.closingBalance)}
            </Typography.Text>
          </List.Item>
        </Card>
      )}

      <OperationEditModal operation={editing} onClose={() => setEditing(null)} />
    </div>
  );
}
